import { FC, ComponentProps, useMemo, Fragment } from 'react';
import { diffWords } from 'diff';
import { shortenNumber } from '@wwe-portal/utils/number';
import { cloneArrayWithId } from '@wwe-portal/utils/array';
import { TextChange } from './text-change';

interface TextDiffProps extends ComponentProps<'div'> {
  oldText: string;
  newText: string;
  showBaseDiff?: boolean;
}

export const TextDiffNumber: FC<TextDiffProps> = ({
  oldText,
  newText,
  showBaseDiff = true,
  ...props
}) => {
  const diff = useMemo(() => {
    const effectiveOldText =
      oldText === '' ? newText.replace(/\d+/g, '0') : oldText;
    const words = diffWords(effectiveOldText, newText);
    let previousRemovedWord: string | undefined = undefined;
    const results: {
      value: string;
      diff: number | null;
      changed: 'removed' | 'added' | 'none';
    }[] = [];
    const handleRemovedNumber = (wordValue: string) => {
      if (previousRemovedWord) {
        results.push({
          value: previousRemovedWord,
          diff: null,
          changed: 'removed',
        });
      }
      previousRemovedWord = wordValue;
    };
    for (const word of words) {
      if (word.removed) {
        handleRemovedNumber(word.value);
        continue;
      }
      if (word.added) {
        const removedNumber = previousRemovedWord
          ? parseInt(previousRemovedWord)
          : 0;
        previousRemovedWord = undefined;
        const addedNumber = parseInt(word.value);

        if (isNaN(removedNumber) || isNaN(addedNumber)) {
          results.push({ value: word.value, diff: null, changed: 'added' });
          continue;
        }

        const diff = addedNumber - removedNumber;
        results.push({
          value: word.value,
          diff,
          changed: diff > 0 ? 'added' : 'removed',
        });
        continue;
      }
      results.push({ value: word.value, diff: null, changed: 'none' });
    }
    handleRemovedNumber('');
    return cloneArrayWithId(results);
  }, [oldText, newText]);

  return (
    <div {...props}>
      {diff.map((item) => {
        return (
          <Fragment key={item.id}>
            <TextChange
              changed={item.changed}
              diff={item.diff}
              enableDiff={showBaseDiff}
            >
              {item.value}
            </TextChange>
            {item.diff != null && (
              <TextChange changed={item.changed} diff={item.diff} enableDiff>
                {' '}
                ({item.diff > 0 ? '+' : ''}
                {shortenNumber(item.diff)}){' '}
              </TextChange>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
